import { addToMailingList } from "@/actions/user"
import { CheckCircle, Loader2 } from "lucide-react"
import { motion } from "motion/react"

import { Button } from "@repo/ui/components/button"
import { Input } from "@repo/ui/components/input"
import { cn } from "@repo/ui/utils"

import { useEffect, useState } from "react"

export function EmailSignup({
  className,
  style
}: {
  className?: string
  style?: React.CSSProperties
}) {
  // State hooks
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [completed, setCompleted] = useState(false)

  // Reset form after submission
  useEffect(() => {
    if (submitted) {
      // Start exit animation after 2 seconds
      const exitTimer = setTimeout(() => {
        setIsExiting(true)
      }, 3000)

      // Reset to form view after 3 seconds total
      const resetTimer = setTimeout(() => {
        setSubmitted(false)
        setIsExiting(false)
        setEmail("")
        setCompleted(true)
      }, 4000)

      // Clean up timers if component unmounts
      return () => {
        clearTimeout(exitTimer)
        clearTimeout(resetTimer)
      }
    }
  }, [submitted])

  // Event handlers
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (email?.length > 0) {
      setLoading(true)
      await addToMailingList(email)
      await new Promise(resolve => setTimeout(resolve, 1000))
      setSubmitted(true)
      setLoading(false)
    }
  }

  if (completed) {
    return null
  }

  // Main render
  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className={cn("flex items-center justify-center gap-2", className)}
      initial={{ opacity: 0, y: 20 }}
      style={style}
      transition={{
        duration: 0.5,
        delay: 0.5
      }}
    >
      {submitted ?
        <motion.div
          animate={isExiting ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
          className="flex items-center justify-center gap-2"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-2 rounded-md bg-white px-2 py-1 text-sm font-medium">
            <CheckCircle className="size-3" />
            <p className="text-sm leading-none font-medium">Submitted</p>
          </div>
        </motion.div>
      : <form className="flex flex-row items-center justify-center gap-2" onSubmit={handleSubmit}>
          {!loading && (
            <div className="relative">
              <Input
                id="email-address"
                placeholder="Get in touch.."
                type="email"
                value={email}
                onBlur={() => setIsFocused(false)}
                onChange={e => setEmail(e.target.value)}
                onFocus={() => setIsFocused(true)}
              />
              {isFocused && (
                <motion.label
                  animate={{ opacity: 1 }}
                  className="text-label-light absolute -bottom-7 left-3 text-sm"
                  htmlFor="email-address"
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Enter your email address
                </motion.label>
              )}
            </div>
          )}
          <Button className="w-auto" type="submit">
            {loading ?
              <Loader2 className="custom-spin size-3 animate-spin" />
            : "→"}
          </Button>
        </form>
      }
    </motion.div>
  )
}
